import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import QuoteRequestModal from './QuoteRequestModal';
import './CategoryPage.css';

const CategoryPage = () => {
  const { subdomain, categorySlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [websiteData, setWebsiteData] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [offers, setOffers] = useState([]);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);

  useEffect(() => {
    loadCategoryData();
  }, [subdomain, categorySlug]);

  const loadCategoryData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch website data and category offers
      const response = await fetch(`/api/public-website/category/${subdomain}/${categorySlug}`);
      
      if (!response.ok) {
        throw new Error('Category not found');
      }

      const data = await response.json();
      setWebsiteData(data.website);
      setCategoryData(data.category);
      setOffers(data.offers || []);
    } catch (err) {
      console.error('Error loading category data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestQuote = (offer = null) => {
    setSelectedOffer(offer);
    setShowQuoteModal(true);
  };

  const formatPrice = (price) => {
    if (!price) return null;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const parseJsonSafely = (jsonString) => {
    if (!jsonString) return [];
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      return [];
    }
  };

  if (loading) {
    return (
      <div className="category-page-loading">
        <div className="loading-spinner">
          <i className="fas fa-spinner fa-spin"></i>
        </div>
        <p>Loading offers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="category-page-error">
        <div className="error-content">
          <i className="fas fa-exclamation-triangle"></i>
          <h2>Category Not Found</h2>
          <p>{error}</p>
          <a href={`/${subdomain}`} className="btn-primary">
            <i className="fas fa-home"></i>
            Back to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="category-page">
      {/* Header Section */}
      <div className="category-header">
        <div className="container">
          <div className="header-content">
            <div className="breadcrumb">
              <a href={`/${subdomain}`}>
                <i className="fas fa-home"></i>
                Home
              </a>
              <i className="fas fa-chevron-right"></i>
              <span>{categoryData?.name}</span>
            </div>
            
            <div className="category-info">
              <div className="category-icon">
                <i className={categoryData?.icon}></i>
              </div>
              <div className="category-details">
                <h1>{categoryData?.name}</h1>
                <p className="category-description">{categoryData?.description}</p>
                <div className="agent-info">
                  <span>Curated by {websiteData?.agentName}</span>
                  {websiteData?.agencyName && (
                    <span> • {websiteData?.agencyName}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Offers Section */}
      <div className="offers-section">
        <div className="container">
          {offers.length === 0 ? (
            <div className="no-offers">
              <div className="no-offers-content">
                <i className="fas fa-search"></i>
                <h3>No Offers Available</h3>
                <p>We're currently updating our {categoryData?.name.toLowerCase()} offers. Please check back soon or contact us for personalized recommendations.</p>
                <button 
                  className="btn-primary"
                  onClick={() => handleRequestQuote()}
                >
                  <i className="fas fa-envelope"></i>
                  Request Custom Quote
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="offers-header">
                <h2>Featured {categoryData?.name} Offers</h2>
                <p>{offers.length} offer{offers.length !== 1 ? 's' : ''} available</p>
              </div>

              <div className="offers-grid">
                {offers.map((offer) => {
                  const highlights = parseJsonSafely(offer.highlights);
                  const images = parseJsonSafely(offer.images);
                  const included = parseJsonSafely(offer.included);
                  
                  return (
                    <div key={offer.id} className="offer-card">
                      {/* Offer Image */}
                      <div className="offer-image">
                        {images.length > 0 ? (
                          <img 
                            src={images[0]} 
                            alt={offer.title}
                            onError={(e) => {
                              e.target.src = '/images/placeholder-offer.jpg';
                            }}
                          />
                        ) : (
                          <div className="image-placeholder">
                            <i className={categoryData?.icon}></i>
                          </div>
                        )}
                        {offer.is_featured && (
                          <div className="featured-badge">
                            <i className="fas fa-star"></i>
                            Featured
                          </div>
                        )}
                      </div>

                      {/* Offer Content */}
                      <div className="offer-content">
                        <div className="offer-header">
                          <h3>{offer.title}</h3>
                          {offer.subcategory_name && (
                            <span className="subcategory-tag">
                              {offer.subcategory_name}
                            </span>
                          )}
                        </div>

                        <p className="offer-description">{offer.short_description}</p>

                        {/* Highlights */}
                        {highlights.length > 0 && (
                          <div className="offer-highlights">
                            <h4>Highlights:</h4>
                            <ul>
                              {highlights.slice(0, 3).map((highlight, index) => (
                                <li key={index}>
                                  <i className="fas fa-check"></i>
                                  {highlight}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* What's Included */}
                        {included.length > 0 && (
                          <div className="offer-included">
                            <h4>What's Included:</h4>
                            <ul>
                              {included.slice(0, 2).map((item, index) => (
                                <li key={index}>
                                  <i className="fas fa-plus-circle"></i>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Pricing and Duration */}
                        <div className="offer-details">
                          {offer.duration && (
                            <div className="offer-duration">
                              <i className="fas fa-clock"></i>
                              <span>{offer.duration}</span>
                            </div>
                          )}
                          
                          {offer.price_from && (
                            <div className="offer-pricing">
                              <span className="price">
                                From {formatPrice(offer.price_from)}
                              </span>
                              {offer.price_note && (
                                <span className="price-note">{offer.price_note}</span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Call to Action */}
                        <div className="offer-actions">
                          <button 
                            className="btn-primary btn-quote"
                            onClick={() => handleRequestQuote(offer)}
                          >
                            <i className="fas fa-paper-plane"></i>
                            Request Free Quote
                          </button>
                          
                          {offer.detailed_description && (
                            <button className="btn-secondary btn-details">
                              <i className="fas fa-info-circle"></i>
                              View Details
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Call to Action Section */}
              <div className="cta-section">
                <div className="cta-content">
                  <h3>Don't See What You're Looking For?</h3>
                  <p>Our travel experts can create a custom {categoryData?.name.toLowerCase()} experience just for you.</p>
                  <button 
                    className="btn-primary btn-large"
                    onClick={() => handleRequestQuote()}
                  >
                    <i className="fas fa-magic"></i>
                    Request Custom Quote
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Contact Section */}
      <div className="contact-section">
        <div className="container">
          <div className="contact-content">
            <div className="contact-info">
              <h3>Ready to Plan Your Trip?</h3>
              <p>Contact {websiteData?.agentName} for personalized service and expert advice.</p>
              
              <div className="contact-methods">
                {websiteData?.agentEmail && (
                  <a href={`mailto:${websiteData.agentEmail}`} className="contact-method">
                    <i className="fas fa-envelope"></i>
                    <span>{websiteData.agentEmail}</span>
                  </a>
                )}
                
                {websiteData?.agentPhone && (
                  <a href={`tel:${websiteData.agentPhone}`} className="contact-method">
                    <i className="fas fa-phone"></i>
                    <span>{websiteData.agentPhone}</span>
                  </a>
                )}
              </div>
            </div>

            <div className="agent-photo">
              {websiteData?.agentPhoto ? (
                <img src={websiteData.agentPhoto} alt={websiteData.agentName} />
              ) : (
                <div className="photo-placeholder">
                  <i className="fas fa-user"></i>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quote Request Modal */}
      <QuoteRequestModal
        isOpen={showQuoteModal}
        onClose={() => {
          setShowQuoteModal(false);
          setSelectedOffer(null);
        }}
        offer={selectedOffer}
        categoryId={categoryData?.id}
        subcategoryId={selectedOffer?.subcategory_id}
        agentId={websiteData?.agentId}
      />
    </div>
  );
};

export default CategoryPage;
